/**
 * Webhook utilities for sending lead data
 */

// Get webhook URL from environment variable or use placeholder
const WEBHOOK_URL = import.meta.env.VITE_WEBHOOK_URL || 'https://example.com/webhook';

/**
 * Send lead data to webhook endpoint
 * @param {Object} data - Lead data to send
 * @returns {Promise<Object>} Response from webhook
 */
export const sendLead = async (data) => {
  try {
    // Get UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source: urlParams.get('utm_source') || 'direct',
      utm_medium: urlParams.get('utm_medium') || 'organic',
      utm_campaign: urlParams.get('utm_campaign') || 'none',
      utm_content: urlParams.get('utm_content') || null,
      utm_term: urlParams.get('utm_term') || null
    };

    // Prepare payload
    const payload = {
      // Form data
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      
      // Property data
      propertyType: data.propertyType,
      address: data.address,
      sqMeters: data.sqMeters,
      rooms: data.rooms,
      bathrooms: data.bathrooms,
      guests: data.guests,
      periodType: data.periodType,
      services: data.services || [],
      condition: data.condition || 'ready',
      
      // Location data
      coordinates: data.coordinates,
      
      // Estimate data
      estimate: data.estimate ? {
        adrBase: data.estimate.adrBase,
        occupancy: data.estimate.occ,
        monthlyMin: data.estimate.monthlyMin,
        monthlyMax: data.estimate.monthlyMax,
        annualMin: data.estimate.annualMin,
        annualMax: data.estimate.annualMax,
        breakdown: data.estimate.breakdown
      } : null,
      
      // Metadata
      action: data.action || 'form_submission',
      timestamp: data.timestamp || new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      url: window.location.href,
      
      // UTM tracking
      ...utmData
    };

    console.log('Sending lead data:', payload);

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Webhook error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Webhook response:', result);

    // Track successful lead submission
    if (typeof gtag !== 'undefined') {
      gtag('event', 'lead_submission', {
        event_category: 'Lead Generation',
        event_label: data.action || 'form_submission',
        value: data.estimate?.monthlyMax || 0
      });
    }

    return result;
    
  } catch (error) {
    console.error('Webhook submission error:', error);
    
    // Track failed lead submission
    if (typeof gtag !== 'undefined') {
      gtag('event', 'lead_submission_failed', {
        event_category: 'Lead Generation',
        event_label: error.message
      });
    }

    throw error;
  }
};

/**
 * Validate lead data before sending
 * @param {Object} data - Lead data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateLeadData = (data) => {
  const errors = [];

  // Required fields
  const requiredFields = {
    firstName: 'Nome',
    lastName: 'Cognome', 
    email: 'Email',
    phone: 'Telefono',
    propertyType: 'Tipo proprietà',
    address: 'Indirizzo'
  };

  Object.entries(requiredFields).forEach(([field, label]) => {
    if (!data[field]) {
      errors.push(`${label} è richiesto`);
    }
  });

  // Email validation
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Formato email non valido');
  }

  // Phone validation (basic)
  if (data.phone && !/^(\+39|0039|\+|00)?[0-9\s\-()]{8,15}$/.test(data.phone)) {
    errors.push('Formato telefono non valido');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Test webhook connection
 * @returns {Promise<boolean>} True if webhook is reachable
 */
export const testWebhook = async () => {
  try {
    const response = await fetch(WEBHOOK_URL, {
      method: 'OPTIONS',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.ok || response.status === 405; // Some webhooks don't support OPTIONS
  } catch (error) {
    console.warn('Webhook test failed:', error);
    return false;
  }
};